import { useState, useEffect } from 'react';
import { WIDGET_VERSION } from '../utils/version';
import { toast } from './use-toast';

const STORAGE_KEY_LAST_CHECK = 'mockdetector-last-version-check';
const STORAGE_KEY_DISMISSED_VERSION = 'mockdetector-dismissed-version';
const STORAGE_KEY_REQUESTED_VERSION = 'mockdetector-requested-version';
const CURRENT_VERSION = WIDGET_VERSION;
const CHECK_INTERVAL_MS = 3 * 60 * 1000; // Check every 3 minutes (faster updates)
const MIN_CHECK_INTERVAL_MS = 5 * 60 * 1000; // Minimum 5 minutes between checks

interface VersionInfo {
  currentVersion: string;
  latestVersion: string | null;
  updateAvailable: boolean;
  isChecking: boolean;
  isUpdating: boolean;
  updateError: string | null;
  lastChecked: Date | null;
}

const getLatestVersionFromNPM = async (): Promise<string | null> => {
  try {
    const response = await fetch('https://registry.npmjs.org/@mockdetector/widget/latest');
    if (!response.ok) return null;
    
    const data = await response.json();
    return data.version || null;
  } catch (error) {
    console.error('MockDetector: Version check failed', error);
    return null;
  }
};

const compareVersions = (current: string, latest: string): boolean => {
  // Remove 'v' prefix if present
  const cleanCurrent = current.replace(/^v/, '');
  const cleanLatest = latest.replace(/^v/, '');
  
  // Split into parts [major, minor, patch]
  const currentParts = cleanCurrent.split('.').map(p => parseInt(p.split('-')[0], 10));
  const latestParts = cleanLatest.split('.').map(p => parseInt(p.split('-')[0], 10));
  
  // Compare major, minor, patch
  for (let i = 0; i < 3; i++) {
    if (latestParts[i] > currentParts[i]) return true;
    if (latestParts[i] < currentParts[i]) return false;
  }
  
  return false; // Versions are equal
};

export const useVersionCheck = () => {
  const [versionInfo, setVersionInfo] = useState<VersionInfo>({
    currentVersion: CURRENT_VERSION,
    latestVersion: null,
    updateAvailable: false,
    isChecking: false,
    isUpdating: false,
    updateError: null,
    lastChecked: null,
  });

  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY_DISMISSED_VERSION);
      return dismissed === CURRENT_VERSION;
    } catch (e) {
      return false;
    }
  });

  const checkForUpdates = async (force = false) => {
    if (versionInfo.isChecking) return { updateAvailable: false, latestVersion: null };
    
    // Check if enough time has passed since last check (unless forced)
    if (!force && typeof window !== 'undefined') {
      try {
        const lastCheck = localStorage.getItem(STORAGE_KEY_LAST_CHECK);
        if (lastCheck) {
          const timeSinceLastCheck = Date.now() - new Date(lastCheck).getTime();
          if (timeSinceLastCheck < MIN_CHECK_INTERVAL_MS) {
            console.log('⏰ MockDetector: Skipping version check (too soon)');
            return { updateAvailable: versionInfo.updateAvailable, latestVersion: versionInfo.latestVersion };
          }
        }
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    
    setVersionInfo(prev => ({ ...prev, isChecking: true }));
    
    const latestVersion = await getLatestVersionFromNPM();
    const now = new Date();
    
    if (latestVersion) {
      const updateAvailable = compareVersions(CURRENT_VERSION, latestVersion);
      
      setVersionInfo({
        currentVersion: CURRENT_VERSION,
        latestVersion,
        updateAvailable,
        isChecking: false,
        isUpdating: false,
        updateError: null,
        lastChecked: now,
      });
      
      // Store last check time
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(STORAGE_KEY_LAST_CHECK, now.toISOString());
        } catch (e) {
          // Ignore localStorage errors
        }
      }
      
      // Reset dismissed flag if new version is available
      if (updateAvailable && typeof window !== 'undefined') {
        try {
          const dismissedVersion = localStorage.getItem(STORAGE_KEY_DISMISSED_VERSION);
          if (dismissedVersion !== latestVersion) {
            setIsDismissed(false);
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }
      
      return { updateAvailable, latestVersion };
    } else {
      setVersionInfo(prev => ({
        ...prev,
        isChecking: false,
        lastChecked: now,
      }));
      
      return { updateAvailable: false, latestVersion: null };
    }
  };

  const dismissUpdate = () => {
    if (versionInfo.latestVersion) {
      localStorage.setItem(STORAGE_KEY_DISMISSED_VERSION, versionInfo.latestVersion);
      setIsDismissed(true);
    }
  };

  const performUpdate = async () => {
    if (!versionInfo.latestVersion || versionInfo.isUpdating) {
      console.log('🚀 MockDetector: Update skipped -', { 
        hasLatestVersion: !!versionInfo.latestVersion, 
        isUpdating: versionInfo.isUpdating 
      });
      return false;
    }
    
    console.log('🚀 MockDetector: Starting update to', versionInfo.latestVersion);
    setVersionInfo(prev => ({ ...prev, isUpdating: true, updateError: null }));
    
    try {
      // FIX 2: Pre-verify CDN availability before reload
      const cdnUrl = `https://cdn.jsdelivr.net/npm/@mockdetector/widget@${versionInfo.latestVersion}/dist/mockdetector-widget.umd.js`;
      console.log('🔍 Verifying CDN availability:', cdnUrl);
      
      const testResponse = await fetch(cdnUrl, { method: 'HEAD' });
      if (!testResponse.ok) {
        throw new Error(`CDN returned ${testResponse.status}: Version might not be published yet`);
      }
      
      console.log('✅ CDN verification passed');
      
      // Store requested version with timestamp
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(STORAGE_KEY_REQUESTED_VERSION, versionInfo.latestVersion);
          localStorage.setItem('mockdetector-update-timestamp', Date.now().toString());
          console.log('💾 MockDetector: Requested version saved to localStorage');
        } catch (e) {
          console.warn('⚠️ MockDetector: Failed to save requested version', e);
        }
      }
      
      // Reload immediately (no need for event dispatch or timeout)
      console.log('🔄 MockDetector: Reloading page to activate new version...');
      window.location.reload();
      
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ MockDetector: Update failed:', errorMessage);
      
      setVersionInfo(prev => ({
        ...prev,
        isUpdating: false,
        updateError: `Update failed: ${errorMessage}. Try again in a few minutes.`,
      }));
      
      return false;
    }
  };

  const copyUpdateCommand = async () => {
    const command = getUpdateCommand();
    
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(command);
        return true;
      } catch (error) {
        console.error('Failed to copy update command:', error);
        return false;
      }
    }
    return false;
  };

  const getUpdateCommand = () => {
    return `npm update @mockdetector/widget@latest`;
  };

  // Check on mount and set up automatic checks every 3 minutes
  useEffect(() => {
    checkForUpdates();
    
    const interval = setInterval(() => {
      checkForUpdates();
    }, CHECK_INTERVAL_MS);
    
    return () => clearInterval(interval);
  }, []);

  // Auto-update when new version detected
  useEffect(() => {
    if (versionInfo.updateAvailable && versionInfo.latestVersion) {
      console.log(`🎉 New version ${versionInfo.latestVersion} detected! Starting automatic update...`);
      
      // Perform update automatically
      performUpdate().then(success => {
        if (success) {
          toast({
            title: "🎉 New version ready!",
            description: "Refreshing page to activate..."
          });
          
          // Auto-reload after 3 seconds
          setTimeout(() => {
            console.log('🔄 Auto-reloading to activate new version...');
            window.location.reload();
          }, 3000);
        } else {
          console.warn('⚠️ Auto-update failed, will retry on next check');
        }
      });
    }
  }, [versionInfo.updateAvailable, versionInfo.latestVersion]);

  return {
    ...versionInfo,
    isDismissed,
    checkForUpdates,
    performUpdate,
    copyUpdateCommand,
    dismissUpdate,
    getUpdateCommand,
    shouldShowNotification: versionInfo.updateAvailable && !isDismissed,
  };
};
